import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import {
  Store,
  User,
  Calendar,
  Clock,
  CheckCircle,
  PauseCircle,
  Package,
  ShoppingBag,
  Users,
  MapPin,
  Mail,
  Phone,
  CreditCard,
} from "lucide-react";
import { Owner } from "../../lib/adminApi";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface OwnerDetailsModalProps {
  owner: Owner | null;
  open: boolean;
  onClose: () => void;
  onActivate: (owner: Owner) => void;
  onSuspend: (owner: Owner) => void;
}

export function ShopDetailsModal({
  owner,
  open,
  onClose,
  onActivate,
  onSuspend,
}: OwnerDetailsModalProps) {
  if (!owner) return null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "No definida";
    try {
      return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy", {
        locale: es,
      });
    } catch {
      return "Fecha inválida";
    }
  };

  const getStatusInfo = () => {
    switch (owner.accountStatus) {
      case "active":
        return {
          color: "green",
          icon: CheckCircle,
          label: "Activo",
          description: "Cuenta activa con suscripción pagada",
        };
      case "trial":
        return {
          color: "yellow",
          icon: Clock,
          label: "En período de prueba",
          description: "Acceso completo durante el trial",
        };
      case "suspended":
        return {
          color: "red",
          icon: PauseCircle,
          label: "Suspendido",
          description: "Cuenta suspendida por falta de pago",
        };
      case "canceled":
        return {
          color: "gray",
          icon: PauseCircle,
          label: "Cancelado",
          description: "Cuenta cancelada permanentemente",
        };
      default:
        return {
          color: "gray",
          icon: Clock,
          label: owner.accountStatus,
          description: "Estado no definido",
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  const getDaysRemaining = () => {
    if (owner.accountStatus !== "trial" || !owner.trialEndDate) return null;

    try {
      const end = new Date(owner.trialEndDate);
      const now = new Date();
      const diffTime = end.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 0) return { text: "Trial expirado", urgent: true };
      if (diffDays <= 3)
        return { text: `${diffDays} días restantes`, urgent: true };
      return { text: `${diffDays} días restantes`, urgent: false };
    } catch {
      return null;
    }
  };

  const daysRemaining = getDaysRemaining();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            {owner.businessName}
          </DialogTitle>
          <DialogDescription>
            Información detallada del negocio
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Estado y acciones */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full bg-${statusInfo.color}-100`}>
                <StatusIcon
                  className={`h-5 w-5 text-${statusInfo.color}-600`}
                />
              </div>
              <div>
                <div className="font-semibold">{statusInfo.label}</div>
                <div className="text-sm text-gray-600">
                  {statusInfo.description}
                </div>
                {daysRemaining && (
                  <div
                    className={`text-sm mt-1 ${
                      daysRemaining.urgent
                        ? "text-red-600 font-medium"
                        : "text-yellow-600"
                    }`}
                  >
                    {daysRemaining.text}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {owner.accountStatus === "suspended" && (
                <Button
                  onClick={() => onActivate(owner)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Activar Cuenta
                </Button>
              )}

              {owner.accountStatus === "active" && (
                <Button
                  variant="outline"
                  onClick={() => onSuspend(owner)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  <PauseCircle className="h-4 w-4 mr-2" />
                  Suspender
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Información básica */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Información Básica</h3>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Dueño</div>
                    <div className="font-medium">{owner.ownerName}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Email</div>
                    <div className="font-medium">{owner.email}</div>
                  </div>
                </div>

                {owner.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-600">Teléfono</div>
                      <div className="font-medium">{owner.phone}</div>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Store className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Tiendas</div>
                    <div className="font-medium">
                      {owner.shopsCount || 1} tienda(s)
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600">Registrado el</div>
                    <div className="font-medium">
                      {formatDate(owner.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Suscripción */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Suscripción</h3>

              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">Plan</div>
                  <div className="font-medium capitalize">
                    {owner.planType || "Básico"}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Estado de pago</div>
                  <div className="font-medium capitalize">
                    {owner.paymentStatus || "Pendiente"}
                  </div>
                </div>

                {owner.trialStartDate && (
                  <div>
                    <div className="text-sm text-gray-600">Trial iniciado</div>
                    <div className="font-medium">
                      {formatDate(owner.trialStartDate)}
                    </div>
                  </div>
                )}

                {owner.trialEndDate && (
                  <div>
                    <div className="text-sm text-gray-600">Trial termina</div>
                    <div className="font-medium">
                      {formatDate(owner.trialEndDate)}
                    </div>
                  </div>
                )}

                {owner.subscriptionEndDate && (
                  <div>
                    <div className="text-sm text-gray-600">
                      Suscripción termina
                    </div>
                    <div className="font-medium">
                      {formatDate(owner.subscriptionEndDate)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Estadísticas */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Estadísticas del Negocio</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Store className="h-4 w-4 text-blue-600" />
                  <div className="text-sm text-gray-600">Tiendas</div>
                </div>
                <div className="text-2xl font-bold">
                  {owner.shopsCount || 1}
                </div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-green-600" />
                  <div className="text-sm text-gray-600">Productos</div>
                </div>
                <div className="text-2xl font-bold">
                  {owner.totalProducts || 0}
                </div>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingBag className="h-4 w-4 text-purple-600" />
                  <div className="text-sm text-gray-600">Ventas totales</div>
                </div>
                <div className="text-2xl font-bold">
                  {owner.totalSales || 0}
                </div>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-4 w-4 text-yellow-600" />
                  <div className="text-sm text-gray-600">Ingresos</div>
                </div>
                <div className="text-2xl font-bold">
                  {owner.totalRevenue || 0} CUP
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Notas */}
          {owner.notes && (
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">Notas</h3>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-gray-700">{owner.notes}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button onClick={onClose}>Ver historial completo</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
